import { createSelector, createSlice } from '@reduxjs/toolkit';

import { logoutUser } from '@/features/auth/authThunks';
import type { RootState } from '@/store/root-state';
import { fetchPropertyMemberProfile, fetchPropertyMembers } from './propertyMembersThunks';
import type { PropertyMembersState } from './propertyMembersTypes';

const EMPTY_PROPERTY_MEMBERS: PropertyMembersState['membersByPropertyID'][string] = [];

const initialState: PropertyMembersState = {
  membersByPropertyID: {},
  profilesByUserID: {},
  fetchingProfilesByUserID: {},
  resolvedProfilesByUserID: {},
  fetchingByPropertyID: {},
  errorByPropertyID: {},
  hasFetchedByPropertyID: {},
};

function toPropertyKey(propertyID: string) {
  return propertyID.trim();
}

const propertyMembersSlice = createSlice({
  name: 'propertyMembers',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPropertyMemberProfile.pending, (state, action) => {
        state.fetchingProfilesByUserID[action.meta.arg.userID] = true;
      })
      .addCase(fetchPropertyMemberProfile.fulfilled, (state, action) => {
        const userID = action.meta.arg.userID;
        state.fetchingProfilesByUserID[userID] = false;
        state.resolvedProfilesByUserID[userID] = true;
        state.profilesByUserID[userID] = action.payload;
      })
      .addCase(fetchPropertyMemberProfile.rejected, (state, action) => {
        if (action.meta.condition) return;
        const userID = action.meta.arg.userID;
        state.fetchingProfilesByUserID[userID] = false;
        state.resolvedProfilesByUserID[userID] = true;
      })
      .addCase(fetchPropertyMembers.pending, (state, action) => {
        const propertyKey = toPropertyKey(action.meta.arg.propertyID);
        if (!propertyKey) return;

        state.fetchingByPropertyID[propertyKey] = true;
        state.errorByPropertyID[propertyKey] = null;
      })
      .addCase(fetchPropertyMembers.fulfilled, (state, action) => {
        const propertyKey = toPropertyKey(action.meta.arg.propertyID);
        if (!propertyKey) return;

        state.fetchingByPropertyID[propertyKey] = false;
        state.errorByPropertyID[propertyKey] = null;
        state.hasFetchedByPropertyID[propertyKey] = true;
        state.membersByPropertyID[propertyKey] = action.payload;
      })
      .addCase(fetchPropertyMembers.rejected, (state, action) => {
        const propertyKey = toPropertyKey(action.meta.arg.propertyID);
        if (!propertyKey || action.meta.condition) return;

        state.fetchingByPropertyID[propertyKey] = false;
        state.errorByPropertyID[propertyKey] =
          action.payload?.message || action.error.message || 'Failed to fetch property members';
        state.hasFetchedByPropertyID[propertyKey] = true;
      })
      .addCase(logoutUser.fulfilled, () => ({ ...initialState }));
  },
});

export default propertyMembersSlice.reducer;

const selectPropertyMembersState = (state: RootState) => state.propertyMembers;

export const selectPropertyMembersByPropertyID = (propertyID?: string) =>
  createSelector(selectPropertyMembersState, (state) => {
    if (!propertyID) return EMPTY_PROPERTY_MEMBERS;
    return state.membersByPropertyID[propertyID.trim()] ?? EMPTY_PROPERTY_MEMBERS;
  });

export const selectPropertyMembersLoadingByPropertyID = (propertyID?: string) =>
  createSelector(selectPropertyMembersState, (state) => {
    if (!propertyID) return false;
    return state.fetchingByPropertyID[propertyID.trim()] ?? false;
  });

export const selectPropertyMembersErrorByPropertyID = (propertyID?: string) =>
  createSelector(selectPropertyMembersState, (state) => {
    if (!propertyID) return null;
    return state.errorByPropertyID[propertyID.trim()] ?? null;
  });

export const selectHasFetchedPropertyMembersByPropertyID = (propertyID?: string) =>
  createSelector(selectPropertyMembersState, (state) => {
    if (!propertyID) return false;
    return state.hasFetchedByPropertyID[propertyID.trim()] ?? false;
  });
